/**
 * Extracts the Contentful entry ID from a content ARI
 *
 * @param contentAri - The content ARI in format: ari:third-party:contentful::entry/{spaceId}/{env}/{entryId}
 * @returns The Contentful entry ID or null if the ARI format is invalid
 */
export const extractContentfulEntryId = (
    contentAri: string | undefined
): string | null => {
    if (!contentAri) {
        return null;
    }

    // Match the pattern: ari:third-party:contentful::entry/{spaceId}/{env}/{entryId}
    const match = contentAri.match(
        /^ari:third-party:contentful::entry\/[^/]+\/[^/]+\/(.+)$/
    );

    if (match && match[1]) {
        return match[1];
    }

    return null;
};

/**
 * Resolves a slug to a full URL using the current hostname
 *
 * @param slug - The slug path (e.g., "/bitbucket-cloud/docs/learn-how-to-use-commits/")
 * @returns The full URL or the slug if running on server-side
 */
export const resolveSlugUrl = (slug: string): string => {
    // Return slug as-is during server-side rendering
    if (typeof window === 'undefined') {
        return slug;
    }

    // Use window.SAC_BASE_URL if available, otherwise fall back to current origin
    const baseUrl = window.SAC_BASE_URL || window.location.origin;

    // Ensure there's a slash between baseUrl and slug
    const normalizedSlug = slug.startsWith('/') ? slug : `/${slug}`;

    return `${baseUrl}${normalizedSlug}`;
};

/**
 * Resolves URL for analytics
 *
 * @param url - The URL to resolve (can be a full URL or a slug)
 * @returns The resolved URL or null if no URL is provided
 */
export const resolveAnalyticsUrl = (url?: string): string | null => {
    if (!url) {
        return null;
    }
    return url.startsWith('http') ? url : resolveSlugUrl(url);
};
